import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Flame, Dumbbell, Droplets, Moon, Activity, ChevronLeft, ChevronRight,
  Calendar, Sparkles, ChevronDown, ChevronUp, AlertTriangle, Pill, Clock,
  Utensils, TrendingDown, Zap, Info
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import axios from "axios";
import { toast } from "sonner";
import { TrackingItem } from "@/components/tracking/TrackingItem";
import CollapsiblePanel from "./CollapsiblePanel";
import DilExplainerModal from "./DilExplainerModal";
import { GoalEditorModal } from "@/components/tracking/GoalEditorModal";

export default function DailyTracking({ dailyData, goals, onUpdate, selectedDate, onDateChange }) {
  const { API, refreshUser, user } = useAuth();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dilExplainerOpen, setDilExplainerOpen] = useState(false);
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [targets, setTargets] = useState(null);
  const [showMicros, setShowMicros] = useState(false);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [editGoals, setEditGoals] = useState(null);
  const [savingGoals, setSavingGoals] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const currentDate = selectedDate || today;
  const isToday = currentDate === today;

  useEffect(() => {
    axios.get(`${API}/tracking/targets`).then(r => setTargets(r.data)).catch(() => {});
  }, [API]);

  const changeDate = (offset) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + offset);
    const newDate = d.toISOString().slice(0, 10);
    const limit = new Date(); limit.setDate(limit.getDate() - 30);
    const limitIso = limit.toISOString().slice(0, 10);
    if (newDate <= today && newDate >= limitIso && onDateChange) onDateChange(newDate);
  };

  const formatDate = (dateStr) => {
    if (dateStr === today) return bg ? "Днес" : "Today";
    const d = new Date(dateStr);
    const y = new Date(today); y.setDate(y.getDate() - 1);
    if (dateStr === y.toISOString().slice(0, 10)) return bg ? "Вчера" : "Yesterday";
    return d.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short" });
  };

  const condition = targets?.condition || "";
  const energy = targets?.energy || {};
  const macros = targets?.macros || {};
  const dil = targets?.dil || {};
  const meal_s = targets?.meal_structure || {};
  const pheno = targets?.phenotype || {};
  const effectiveCals = macros.calories || goals.calories;

  const saveCustomGoals = async () => {
    if (!editGoals) return;
    setSavingGoals(true);
    try {
      const { weight_kg, weekly_target_kg, ...macroGoals } = editGoals;
      // iter366 — push weight + weeklyTargetKg to the profile (top-level
      // keys consumed by the backend update_profile handler), and the
      // pure macro goals under `goals` (legacy override path).
      const payload = { goals: macroGoals };
      if (weight_kg !== null && weight_kg !== undefined && !Number.isNaN(weight_kg) && weight_kg > 0) {
        payload.weight = weight_kg;
      }
      if (weekly_target_kg !== null && weekly_target_kg !== undefined) {
        payload.weeklyTargetKg = weekly_target_kg;
      }
      await axios.put(`${API}/user/profile`, payload);
      await refreshUser();
      toast.success(bg ? "Целите са обновени!" : "Goals updated!");
      setShowGoalEditor(false);
      axios.get(`${API}/tracking/targets`).then(r => setTargets(r.data)).catch(() => {});
    } catch {
      toast.error(bg ? "Грешка" : "Error");
    } finally {
      setSavingGoals(false);
    }
  };

  const openGoalEditor = () => {
    const userProfile = (user?.profile) || {};
    const bodySec = userProfile.body || {};
    const lifestyle = userProfile.lifestyle || {};
    const personal = userProfile.personal || {};
    setEditGoals({
      calories: effectiveCals || 2000,
      protein: macros.protein || goals.protein || 150,
      carbs: macros.carbs || goals.carbs || 200,
      fat: macros.fat || goals.fat || 65,
      // iter366 — preload current profile values
      weight_kg: personal.weight_kg || bodySec.weight || targets?.weight || null,
      weekly_target_kg: lifestyle.weeklyTargetKg ?? 0,
    });
    setShowGoalEditor(true);
  };

  const baseItems = [
    { key: "calories", icon: Flame, label: bg ? "Калории" : "Calories", unit: "kcal", color: "#EF4444", step: 100, goal: effectiveCals,
      hint: energy.tdee ? `TDEE: ${energy.tdee} → ${bg ? "Финални" : "Final"}: ${energy.final_calories} kcal` : null },
    { key: "protein", icon: Dumbbell, label: bg ? "Протеин" : "Protein", unit: "g", color: "#10B981", step: 10, goal: macros.protein || goals.protein,
      hint: targets ? `${targets.protein_per_kg}g/kg x ${targets.weight}kg` : null },
    { key: "carbs", icon: Activity, label: bg ? "Въглехидрати" : "Carbs", unit: "g", color: "#F59E0B", step: 20, goal: macros.carbs || goals.carbs },
    { key: "fat", icon: Droplets, label: bg ? "Мазнини" : "Fat", unit: "g", color: "#6366F1", step: 5, goal: macros.fat || goals.fat },
  ];

  const conditionItems = [];
  if (macros.saturated_fat) conditionItems.push({ key: "saturated_fat", icon: AlertTriangle, label: bg ? "Наситени мазнини" : "Sat Fat", unit: "g", color: "#DC2626", step: 2, goal: macros.saturated_fat, hint: `${macros.sat_fat_pct}% x ${effectiveCals} kcal / 9 = ${macros.saturated_fat}g` });
  if (macros.sugars) conditionItems.push({ key: "sugars", icon: Sparkles, label: bg ? "Захари" : "Sugars", unit: "g", color: "#EC4899", step: 5, goal: macros.sugars, hint: `<10% x ${effectiveCals} kcal / 4 = ${macros.sugars}g` });

  const lifestyleItems = [
    { key: "water", icon: Droplets, label: bg ? "Вода" : "Water", unit: bg ? "чаши" : "cups", color: "#0EA5E9", step: 1, goal: goals.water,
      calcKey: "water_intake", calcLabel: bg ? "Изчисли от калкулатор" : "Calculate from health calc" },
    { key: "sleep", icon: Moon, label: bg ? "Сън" : "Sleep", unit: bg ? "часа" : "hrs", color: "#8B5CF6", step: 0.5, goal: goals.sleep,
      calcKey: "sleep", calcLabel: bg ? "Изчисли от калкулатор" : "Calculate from health calc" },
    { key: "activity", icon: Activity, label: bg ? "Физическа активност" : "Activity", unit: bg ? "мин" : "min", color: "#EC4899", step: 10, goal: goals.activity, hint: bg ? "Физическите усилия намаляват нетните калории" : "Exercise reduces net calories" },
  ];

  // Fiber — goal from calculator, not AI
  const fiberItem = { key: "fiber", icon: Zap, label: bg ? "Фибри" : "Fiber", unit: "g", color: "#059669", step: 5,
    goal: goals.fiber || (targets?.sex === "female" ? 25 : 38),
    calcKey: "fiber", calcLabel: bg ? "Изчисли от калкулатор" : "Calculate from health calc" };

  const allItems = [...baseItems, ...conditionItems, fiberItem, ...lifestyleItems];

  const microLabels = {
    magnesium: bg ? "Магнезий" : "Magnesium", zinc: bg ? "Цинк" : "Zinc",
    chromium: bg ? "Хром" : "Chromium", selenium: bg ? "Селен" : "Selenium",
    copper: bg ? "Мед" : "Copper", iron: bg ? "Желязо" : "Iron",
    calcium: bg ? "Калций" : "Calcium", potassium: bg ? "Калий" : "Potassium",
    iodine: bg ? "Йод" : "Iodine", boron: bg ? "Бор" : "Boron",
    vitamin_d: bg ? "Витамин D" : "Vitamin D", vitamin_k: bg ? "Витамин K" : "Vitamin K",
    folate: bg ? "Фолат" : "Folate", vitamin_c: bg ? "Витамин C" : "Vitamin C",
    vitamin_b6: bg ? "Витамин B6" : "Vitamin B6", methionine: bg ? "Метионин" : "Methionine",
  };

  const sortedMicros = useMemo(() => {
    if (!targets?.micros) return [];
    return Object.entries(targets.micros)
      .sort(([,a],[,b]) => (b.priority === true ? 1 : 0) - (a.priority === true ? 1 : 0));
  }, [targets?.micros]);

  const openCalculator = (calcKey) => {
    // Map calc keys to test IDs
    const calcTestIds = {
      water_intake: "water-intake-calc-btn",
      fiber: "fiber-calc-btn",
      sleep: "sleep-calc-btn",
    };
    const testId = calcTestIds[calcKey];
    if (!testId) return;

    // First try same-tab (desktop grid, or already-visible panel).
    const existing = document.querySelector(`[data-testid="${testId}"]`);
    if (existing) {
      existing.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => existing.click(), 400);
      return;
    }

    // Cross-tab: the calculator panel lives in a different mobile section.
    // Dashboard listens for this event, switches to the correct tab, then we
    // retry the DOM lookup after the panel mounts.
    window.dispatchEvent(new CustomEvent("dashboard:open-calc", { detail: { calcKey, testId } }));
  };

  const editGoalsBadge = (
    <button onClick={(e) => { e.stopPropagation(); openGoalEditor(); }} className={`text-[10px] font-medium px-2 py-1 rounded-lg ${isDark ? 'text-gray-400 hover:bg-[hsl(223,47%,15%)]' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="edit-goals-btn">
      {bg ? "Цели" : "Goals"}
    </button>
  );

  return (
    <>
    <CollapsiblePanel
      title={bg ? "Дневно проследяване" : "Daily Tracking"}
      icon={Activity}
      iconColor="#0EA5E9"
      credits={0}
      badge={editGoalsBadge}
      defaultOpen={true}
      storageKey="daily-tracking"
      testId="daily-tracking-card"
    >
      <div>
        {/* Date Navigator */}
        <div className="flex items-center justify-center gap-2 mb-2" data-testid="tracking-date-nav">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => changeDate(-1)} data-testid="tracking-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-[hsl(223,47%,12%)] text-gray-200 hover:bg-[hsl(223,47%,18%)]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid="tracking-date-picker-btn"
                aria-label={bg ? "Избор на дата" : "Pick date"}
              >
                <Calendar className="w-3 h-3" /> {formatDate(currentDate)}
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center" data-testid="tracking-date-picker-popover">
              <CalendarWidget
                mode="single"
                selected={new Date(currentDate)}
                onSelect={(d) => {
                  if (!d) return;
                  const iso = d.toISOString().slice(0, 10);
                  if (iso <= today && onDateChange) { onDateChange(iso); setCalendarOpen(false); }
                }}
                disabled={(d) => {
                  const iso = d.toISOString().slice(0, 10);
                  // Allow up to 30 days in the past, never future
                  const limit = new Date(); limit.setDate(limit.getDate() - 30);
                  return iso > today || d < limit;
                }}
                initialFocus
              />
              <div className={`flex items-center justify-between gap-2 px-3 py-2 border-t text-[11px] ${isDark ? 'border-[hsl(223,47%,18%)] text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                <span>{bg ? "До 30 дни назад" : "Up to 30 days back"}</span>
                <button
                  type="button"
                  className={`underline ${isDark ? 'text-sky-400' : 'text-sky-600'} hover:opacity-80`}
                  onClick={() => { onDateChange && onDateChange(today); setCalendarOpen(false); }}
                  data-testid="tracking-date-today-btn"
                >
                  {bg ? "Днес" : "Today"}
                </button>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => changeDate(1)} disabled={isToday} data-testid="tracking-next-day"><ChevronRight className="w-4 h-4" /></Button>
        </div>

        {/* Energy + Condition Summary */}
        {targets && (
          <div className={`mb-3 p-2.5 rounded-xl space-y-1.5 ${isDark ? 'bg-[hsl(223,47%,10%)]' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`} data-testid="energy-summary">
            <div className="flex items-center justify-between text-[10px]">
              <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {condition && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#6366F1]/10 text-[#6366F1] font-semibold mr-1" data-testid="condition-badge">{bg ? ({
                  "general_health": "Общо състояние", "weight_loss": "Намаляване на тегло", "muscle_gain": "Покачване на мускулна маса",
                  "insulin_resistance": "Контрол на кръвната захар", "diabetes_type2": "Подкрепа при висока кръвна захар",
                  "hormonal_imbalance": "Баланс на хормоните", "hormone_imbalance": "Баланс на хормоните",
                  "adrenal_fatigue": "Чувствителен към стрес", "metabolic_slowdown": "Бавен метаболизъм",
                  "inflammatory": "Намаляване на възпалението", "inflammation": "Намаляване на възпалението",
                  "maintenance": "Поддържане на състоянието",
                }[condition] || condition.replace(/_/g, " ")) : ({
                  "general_health": "General health", "weight_loss": "Weight loss", "muscle_gain": "Muscle gain",
                  "insulin_resistance": "Blood sugar control", "diabetes_type2": "High blood sugar support",
                  "hormonal_imbalance": "Hormone balance", "hormone_imbalance": "Hormone balance",
                  "adrenal_fatigue": "Stress sensitive", "metabolic_slowdown": "Slow metabolism",
                  "inflammatory": "Reducing inflammation", "inflammation": "Reducing inflammation",
                  "maintenance": "Maintenance",
                }[condition] || condition.replace(/_/g, " "))}</span>}
                {/* iter273: P-code chip (P1-P5 from DIL engine) скрит за пациент-facing
                    UI — показвахме го заедно с condition tag, което объркваше
                    ("Контрол на кръвната захар P3"). Composite phenotype card
                    показва тази информация в по-човешка форма. Запазваме за
                    clinician/admin views при нужда — там се рендира с тага. */}
              </span>
              {dil.personalized_dil > 0 && (
                <span className={`flex items-center gap-1 font-semibold ${isDark ? 'text-[#F59E0B]' : 'text-[#D97706]'}`}>
                  DIL: {dil.personalized_dil}
                  <button
                    type="button"
                    onClick={() => setDilExplainerOpen(true)}
                    title={bg ? "Защо този DIL?" : "Why this DIL?"}
                    aria-label={bg ? "Защо този DIL?" : "Why this DIL?"}
                    data-testid="dil-explain-trigger-tracking"
                    className={`inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-amber-500/20 transition-colors`}
                  >
                    <Info className="w-3 h-3 opacity-70" />
                  </button>
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>BMR {energy.bmr} → TDEE {energy.tdee} → {bg ? "Финал" : "Final"} {energy.final_calories} kcal</span>
              {energy.weekly_change_kg !== 0 && (
                <span className={`flex items-center gap-0.5 font-medium ${energy.weekly_change_kg > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  <TrendingDown className="w-3 h-3" />{energy.weekly_change_kg > 0 ? "-" : "+"}{Math.abs(energy.weekly_change_kg)} kg/{bg ? "сед" : "wk"}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3 pt-0">
        {allItems.map((item) => (
          <TrackingItem key={item.key} icon={item.icon} label={item.label} value={dailyData[item.key] || 0} goal={item.goal} unit={item.unit}
            color={item.color} isDark={isDark} isToday={isToday} hint={item.hint}
            calcKey={item.calcKey} calcLabel={item.calcLabel} onOpenCalc={openCalculator}
            onIncrement={() => onUpdate(item.key, (dailyData[item.key] || 0) + item.step)}
            onDecrement={() => onUpdate(item.key, Math.max(0, (dailyData[item.key] || 0) - item.step))} />
        ))}

        {/* Meal Structure + Fasting */}
        {meal_s.recommended_meals && (
          <div className={`border-t pt-2.5 ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`}>
            <div className="flex items-center gap-4 text-[10px]">
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-help ${isDark ? 'bg-[hsl(223,47%,12%)]' : 'bg-orange-50'}`}>
                      <Utensils className="w-3 h-3 text-[#F59E0B]" />
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{meal_s.recommended_meals} {bg ? "хранения" : "meals"}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-[200px]">{bg ? `Препоръчителен диапазон: ${meal_s.meal_range?.[0]}-${meal_s.meal_range?.[1]} хранения/ден за вашия профил` : `Recommended range: ${meal_s.meal_range?.[0]}-${meal_s.meal_range?.[1]} meals/day for your profile`}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-help ${isDark ? 'bg-[hsl(223,47%,12%)]' : 'bg-purple-50'}`}>
                      <Clock className="w-3 h-3 text-[#8B5CF6]" />
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{meal_s.fasting_hours}h {bg ? "фастинг" : "fast"} / {meal_s.eating_window}h {bg ? "хранене" : "eating"}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-[200px]">{bg ? `Диапазон: ${meal_s.fasting_range?.[0]}-${meal_s.fasting_range?.[1]}ч фастинг за вашия профил` : `Range: ${meal_s.fasting_range?.[0]}-${meal_s.fasting_range?.[1]}h fasting for your profile`}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}

        {/* Micronutrients */}
        {targets?.micros && (
          <div className={`border-t pt-2 ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`}>
            <button onClick={() => setShowMicros(!showMicros)} className={`flex items-center justify-between w-full text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-testid="toggle-micros-btn">
              <span className="flex items-center gap-1.5"><Pill className="w-3.5 h-3.5 text-[#8B5CF6]" />{bg ? "Микроелементи и витамини" : "Micronutrients & Vitamins"}</span>
              {showMicros ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showMicros && (
              <div className={`mt-1.5 p-2.5 rounded-xl space-y-0.5 ${isDark ? 'bg-[hsl(223,47%,10%)]' : 'bg-gray-50'}`} data-testid="micros-panel">
                {sortedMicros.map(([key, micro]) => (
                  <div key={key} className={`flex items-center justify-between py-1 ${micro.priority === true ? '' : 'opacity-50'}`}>
                    <span className={`text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{microLabels[key] || key}</span>
                    <span className={`text-[10px] font-medium ${micro.priority === true ? 'text-[#0EA5E9]' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {typeof micro.target === 'number' ? (micro.target % 1 === 0 ? micro.target : micro.target.toFixed(1)) : micro.target} {micro.unit}
                      {micro.priority === true && <AlertTriangle className="w-2.5 h-2.5 inline text-[#F59E0B] ml-0.5" />}
                    </span>
                  </div>
                ))}
                <p className={`text-[9px] mt-1.5 pt-1.5 border-t ${isDark ? 'text-gray-500 border-[hsl(223,47%,18%)]' : 'text-gray-400 border-gray-200'}`}>
                  {/* iter273: P-code chip скрит в pacient-facing UI; Composite phenotype card
                      показва същата информация в по-човешка форма. */}
                  {bg ? `Адаптирано за: ${condition.replace(/_/g, " ")}` : `Adapted for: ${condition.replace(/_/g, " ")}`}
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </CollapsiblePanel>

    <GoalEditorModal open={showGoalEditor} onClose={() => setShowGoalEditor(false)}
      editGoals={editGoals} setEditGoals={setEditGoals} onSave={saveCustomGoals}
      saving={savingGoals} isDark={isDark} bg={bg}
      baselineCalories={energy.tdee || effectiveCals} />
    <DilExplainerModal open={dilExplainerOpen} onClose={() => setDilExplainerOpen(false)} />
    </>
  );
}
